import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import { convertMinutesToHoursAndMinutes } from 'lib/flights/flightUtils'
import { parseScheduleChangeDateTime } from 'lib/flights/scheduleChangePresentation'
import ScheduleChangeFareRules from './ScheduleChangeFareRules'
import ScheduleChangeSnapshotFlightDetails from './ScheduleChangeSnapshotFlightDetails'
import type { ScheduleChangeFareRulesSelection } from './scheduleChangeDetailsTypes'

interface Props {
  leg: App.FlightScheduleChangeLeg
  showRoute?: boolean
  bookingFareRules?: App.FlightScheduleChange['fareRules']
  onShowFareRules: (selection: ScheduleChangeFareRulesSelection) => void
}

function ScheduleChangeJourneyDetails({ leg, bookingFareRules, onShowFareRules, showRoute = true }: Props) {
  const intl = useIntl()
  const first = leg.flights[0]
  const last = leg.flights[leg.flights.length - 1]
  return <VerticalSpacer gap={12}>
    {showRoute && first && last && <Heading variant="heading6">{intl.formatMessage({
      id: 'k0YJYWB0UhTuPqO4kNS6',
      defaultMessage: '{departureAirport} ({departureCode}) to {arrivalAirport} ({arrivalCode})',
      description: 'Heading showing flight route in details view in the flights flow',
    }, {
      departureAirport: first.departureAirportName ?? first.departureAirport,
      departureCode: first.departureAirport,
      arrivalAirport: last.arrivalAirportName ?? last.arrivalAirport,
      arrivalCode: last.arrivalAirport,
    })}
    </Heading>}
    {leg.flights.map((flight, index) => {
      const previous = leg.flights[index - 1]
      const minutes = previous?.arrivalAirport && previous.arrivalAirport === flight.departureAirport
        ? parseScheduleChangeDateTime(flight.departureDateTime).diff(parseScheduleChangeDateTime(previous.arrivalDateTime), 'minutes')
        : undefined
      const layoverDuration = minutes !== undefined && minutes >= 0 ? convertMinutesToHoursAndMinutes(minutes) : undefined
      return <VerticalSpacer gap={8} key={flight.id ?? `${flight.departureAirport}-${flight.departureDateTime}-${index}`}>
        {layoverDuration && <MessageBanner kind="warning" icon={<LineClockFiveIcon />}>{intl.formatMessage({
          id: 'im2eAqvC2ssKq2BCncma',
          defaultMessage: '{hours}h {minutes}m layover in {airportName} ({airportCode})',
          description: 'Stopover duration and location in flight details in the flights section',
        }, { hours: layoverDuration.hours, minutes: layoverDuration.minutes, airportName: flight.departureAirportName ?? flight.departureAirport, airportCode: flight.departureAirport })}
        </MessageBanner>}
        {previous?.arrivalAirport && flight.departureAirport && previous.arrivalAirport !== flight.departureAirport && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>{intl.formatMessage({
          id: 'Z8-rLkFJElDt6dRqwlkL',
          defaultMessage: 'Change airports from {arrivalAirport} to {departureAirport} for your connecting flight.',
          description: 'Warning between connecting flight segments when the next flight departs from a different airport',
        }, { arrivalAirport: previous.arrivalAirport, departureAirport: flight.departureAirport })}
        </MessageBanner>}
        <ScheduleChangeSnapshotFlightDetails flight={flight} leg={leg} index={index} onShowFareRules={onShowFareRules} />
      </VerticalSpacer>
    })}
    {!!bookingFareRules?.length && <VerticalSpacer gap={12}>
      <Heading variant="heading6">{intl.formatMessage({
        id: '9kYpbQDXlzh574Eh5ZY0',
        defaultMessage: 'Booking fare conditions',
        description: 'Heading for captured schedule change fare conditions that may cover multiple flight legs',
      })}
      </Heading>
      <ScheduleChangeFareRules rules={bookingFareRules} />
    </VerticalSpacer>}
  </VerticalSpacer>
}

export default ScheduleChangeJourneyDetails
